import React, { useEffect } from "react";
import { Helmet } from "react-helmet";

const SEO = ({
    title = "MINFERT - Agricultural Solutions",
    description = "Premium agricultural products and solutions",
    keywords = "",
    tags = "",
    canonical = "",
    ogTitle = "",
    ogDescription = "",
    ogImage = "",
    ogUrl = "",
    ogType = "website",
    twitterCard = "summary_large_image",
    twitterTitle = "",
    twitterDescription = "",
    twitterImage = "",
    robots = "index,follow",
    author = "",

    FAQSchema = null,
    structuredData = null,
    headSchema = "",  // String containing multiple <script> tags
    bodySchema = ""   // String containing multiple <script> tags
}) => {

    const processArrayOrString = (data) => {
        if (!data) return "";
        if (typeof data === "string") {
            const trimmed = data.trim();

            if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
                try {
                    const parsed = JSON.parse(trimmed);
                    return processArrayOrString(parsed); // re-process as real array
                } catch {
                    return data; // fallback plain string
                }
            }

            return data;
        }

        if (Array.isArray(data)) {
            if (typeof data[0] === "object") {
                return data
                    .map(i => i.text || i.label || i.name)
                    .filter(Boolean)
                    .join(", ");
            }

            return data.join(", ");
        }

        return "";
    };


    const safeParseFaq = (str) => {
        try {
            return JSON.parse(str);
        } catch {
            try {
                const fixed = str
                    .replace(/(\w+)\s*:/g, '"$1":')
                    .replace(/'/g, '"');
                return JSON.parse(fixed);
            } catch {
                return null;
            }
        }
    };

    const buildFAQSchema = (faq) => {
        if (!faq) return null;

        let parsedFaq = faq;

        if (typeof faq === "string") {
            parsedFaq = safeParseFaq(faq);
            if (!parsedFaq) return null;
        }

        if (!Array.isArray(parsedFaq) || parsedFaq.length === 0) {
            return null;
        }

        return {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": parsedFaq.map(item => ({
                "@type": "Question",
                "name": item.question,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": item.answer
                }
            }))
        };
    };

    const faqSchemaJson = buildFAQSchema(FAQSchema);

    // Inject head scripts
    useEffect(() => {
        if (!headSchema || typeof headSchema !== "string" || headSchema.trim() === "") return;

        const range = document.createRange();
        const fragment = range.createContextualFragment(headSchema);
        const nodes = Array.from(fragment.childNodes);

        // Append all nodes to head
        nodes.forEach(node => {
            if (node.nodeType === 1 || node.nodeType === 3) { // Element or Text node
                document.head.appendChild(node.cloneNode(true));
            }
        });

        // Store cloned nodes for cleanup
        const addedNodes = Array.from(document.head.querySelectorAll('script')).slice(-nodes.filter(n => n.nodeName === 'SCRIPT').length);

        return () => {
            addedNodes.forEach(node => {
                if (document.head.contains(node)) {
                    document.head.removeChild(node);
                }
            });
        };
    }, [headSchema]);

    // Inject body scripts
    useEffect(() => {
        if (!bodySchema || typeof bodySchema !== "string" || bodySchema.trim() === "") return;

        const range = document.createRange();
        const fragment = range.createContextualFragment(bodySchema);
        const nodes = Array.from(fragment.childNodes);

        // Insert all nodes at the beginning of body
        const scriptElements = [];
        nodes.reverse().forEach(node => {
            if (node.nodeType === 1 || node.nodeType === 3) { // Element or Text node
                const clonedNode = node.cloneNode(true);
                document.body.insertBefore(clonedNode, document.body.firstChild);
                scriptElements.push(clonedNode);
            }
        });

        return () => {
            scriptElements.forEach(element => {
                if (document.body.contains(element)) {
                    document.body.removeChild(element);
                }
            });
        };
    }, [bodySchema]);

    // FAQ Schema injection
    useEffect(() => {
        if (!faqSchemaJson) return;

        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.text = JSON.stringify(faqSchemaJson);

        document.body.insertBefore(script, document.body.firstChild);

        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, [faqSchemaJson]);

    const processedKeywords = processArrayOrString(keywords);
    const processedTags = processArrayOrString(tags);

    const baseUrl =
        window.location.hostname === "localhost"
            ? "http://localhost:3000"
            : "https://minfert.in";

    const canonicalUrl = canonical || `${baseUrl}${window.location.pathname}`;

    const finalOgTitle = ogTitle || title;
    const finalOgDescription = ogDescription || description;
    const finalOgUrl = ogUrl || canonicalUrl;
    const finalOgImage = ogImage || `${baseUrl}/assets/images/home/logo.webp`;

    const finalTwitterTitle = twitterTitle || finalOgTitle;
    const finalTwitterDescription = twitterDescription || finalOgDescription;
    const finalTwitterImage = twitterImage || finalOgImage;

    return (
        <Helmet>
            <title>{title}</title>
            <meta name="title" content={title} />
            <meta name="description" content={description} />
            {processedKeywords && <meta name="keywords" content={processedKeywords} />}
            {processedTags && <meta name="tags" content={processedTags} />}
            {author && <meta name="author" content={author} />}
            <meta name="robots" content={robots} />
            <link rel="canonical" href={canonicalUrl} />

            <meta property="og:type" content={ogType} />
            <meta property="og:url" content={finalOgUrl} />
            <meta property="og:title" content={finalOgTitle} />
            <meta property="og:description" content={finalOgDescription} />
            <meta property="og:image" content={finalOgImage} />

            <meta name="twitter:card" content={twitterCard} />
            <meta name="twitter:title" content={finalTwitterTitle} />
            <meta name="twitter:description" content={finalTwitterDescription} />
            <meta name="twitter:image" content={finalTwitterImage} />

            {structuredData && (
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            )}
        </Helmet>
    );
};

export default SEO;
